/**
 * <ClarificationBoard>
 *
 * Auto-reply Needs Manual 卡片：调用 listClarificationsGet 接口获取卡涩问题列表并渲染。
 * 点击某一行 → 打开 ClarificationReplyDialog，把列表数据 + 选中项传递给弹窗。
 *
 * 空状态 UI 基于 Figma 节点 9942-136299 还原：
 *   居中显示 circle-alert 图标 + "No Tasks yet" 文案。
 *
 * 基于 Figma 节点 9281-13870 还原。
 */

import { FC, useCallback, useEffect, useState } from 'react';
import cn from 'classnames';
import { CircleAlert } from 'lucide-react';
import { listClarificationsGet } from '@/services/swagger/easourcing/TaskReplyService';
import type { IApiClarificationResp } from '@/services/swagger/easourcing/models';
import ClarificationReplyDialog from '../clarification-reply-dialog';

export type ClarificationBoardProps = {
  inquiryTaskId: string;
  /** 回复完成后的外部刷新回调 */
  onReplied?: () => void;
  /** 外部递增此值可触发列表刷新 */
  refreshKey?: number;
  className?: string;
};

export const ClarificationBoard: FC<ClarificationBoardProps> = ({
  inquiryTaskId,
  onReplied,
  refreshKey,
  className,
}) => {
  // ---------------------------------------------------------------------------
  // 列表数据
  // ---------------------------------------------------------------------------
  const [items, setItems] = useState<IApiClarificationResp[]>([]);
  const [loading, setLoading] = useState(false);

  const fetchList = useCallback(async () => {
    if (!inquiryTaskId) return;
    setLoading(true);
    try {
      const res = await listClarificationsGet({ inquiryTaskId });
      setItems(res?.data ?? []);
    } catch {
      console.error('Failed to fetch clarifications');
    } finally {
      setLoading(false);
    }
  }, [inquiryTaskId]);

  useEffect(() => {
    void fetchList();
  }, [fetchList]);

  // 外部通过递增 refreshKey 触发重新拉取
  useEffect(() => {
    if (refreshKey) {
      void fetchList();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [refreshKey]);

  // ---------------------------------------------------------------------------
  // 弹窗状态
  // ---------------------------------------------------------------------------
  const [dialogOpen, setDialogOpen] = useState(false);
  const [selectedId, setSelectedId] = useState<string | null>(null);

  const handleClickRow = useCallback((item: IApiClarificationResp) => {
    setSelectedId(item.clarificationId);
    setDialogOpen(true);
  }, []);

  const handleDialogClose = useCallback(() => {
    setDialogOpen(false);
    setSelectedId(null);
  }, []);

  const handleItemReplied = useCallback(
    (repliedId: string) => {
      setItems(prev => prev.filter(i => i.clarificationId !== repliedId));
      onReplied?.();
    },
    [onReplied]
  );

  const handleAllDone = useCallback(() => {
    setDialogOpen(false);
    setSelectedId(null);
    setItems([]);
    onReplied?.();
  }, [onReplied]);

  return (
    <>
      <section
        className={cn(
          'h-full border border-[#E5E7EB] rounded-lg py-4 px-0 flex flex-col gap-5 relative overflow-hidden',
          className
        )}
      >
        {/* 背景装饰光晕（Figma 中有渐变 blur 装饰） */}
        <div
          className="absolute -top-[91px] left-0 w-[504px] h-[270px] opacity-60 pointer-events-none"
          style={{
            background:
              'radial-gradient(circle at 30% 40%, rgba(55, 228, 166, 0.15) 0%, transparent 60%), radial-gradient(circle at 70% 50%, rgba(196, 237, 148, 0.12) 0%, transparent 60%)',
          }}
        />

        {/* Header 行 */}
        <div className="flex items-center justify-between relative z-10 px-4">
          <div className="flex items-center gap-2">
            {/* <Astroid size={18} strokeWidth={1.75} className="text-[#A3A3A3]" /> */}
            <svg width="18" height="18" viewBox="0 0 18 18" fill="none" xmlns="http://www.w3.org/2000/svg">
              <g clip-path="url(#clip0_9929_135461)">
                <path
                  d="M7.45258 11.6252C7.38562 11.3656 7.25034 11.1288 7.0608 10.9392C6.87125 10.7497 6.63439 10.6144 6.37483 10.5474L1.77358 9.36094C1.69508 9.33865 1.62599 9.29137 1.57679 9.22627C1.52759 9.16117 1.50098 9.08179 1.50098 9.00019C1.50098 8.91858 1.52759 8.83921 1.57679 8.7741C1.62599 8.709 1.69508 8.66172 1.77358 8.63944L6.37483 7.45219C6.63429 7.38529 6.8711 7.25012 7.06063 7.06072C7.25017 6.87131 7.38551 6.6346 7.45258 6.37519L8.63908 1.77394C8.66114 1.69513 8.70837 1.62569 8.77357 1.57623C8.83878 1.52677 8.91837 1.5 9.00021 1.5C9.08205 1.5 9.16164 1.52677 9.22684 1.57623C9.29204 1.62569 9.33928 1.69513 9.36133 1.77394L10.5471 6.37519C10.614 6.63474 10.7493 6.87161 10.9389 7.06115C11.1284 7.25069 11.3653 7.38598 11.6248 7.45294L16.2261 8.63869C16.3052 8.66051 16.375 8.70769 16.4247 8.77299C16.4744 8.83829 16.5014 8.91811 16.5014 9.00019C16.5014 9.08227 16.4744 9.16208 16.4247 9.22738C16.375 9.29268 16.3052 9.33986 16.2261 9.36169L11.6248 10.5474C11.3653 10.6144 11.1284 10.7497 10.9389 10.9392C10.7493 11.1288 10.614 11.3656 10.5471 11.6252L9.36058 16.2264C9.33853 16.3052 9.29129 16.3747 9.22609 16.4241C9.16089 16.4736 9.0813 16.5004 8.99946 16.5004C8.91762 16.5004 8.83803 16.4736 8.77282 16.4241C8.70762 16.3747 8.66039 16.3052 8.63833 16.2264L7.45258 11.6252Z"
                  stroke="#A3A3A3"
                  stroke-width="1.125"
                  stroke-linecap="round"
                  stroke-linejoin="round"
                />
              </g>
              <defs>
                <clipPath id="clip0_9929_135461">
                  <rect width="18" height="18" fill="white" />
                </clipPath>
              </defs>
            </svg>

            <h2 className="text-base font-bold text-[#101828]">Auto-reply requires manual intervention</h2>
          </div>
          <button
            type="button"
            onClick={() => {
              setSelectedId(items.length > 0 ? items[0].clarificationId : null);
              setDialogOpen(true);
            }}
            disabled={items.length === 0}
            className="bg-[#171717] text-white rounded-lg px-3 py-2 text-xs font-medium leading-none hover:bg-[#171717]/90 transition-colors disabled:opacity-50 disabled:cursor-not-allowed cursor-pointer !rounded-[8px]"
          >
            Replied
          </button>
        </div>

        {/* 列表区域 */}
        <div className="flex-1 relative z-10 overflow-y-auto rounded-lg max-h-[134px] mt-5">
          {loading ? (
            <div className="flex items-center justify-center h-full min-h-[80px]">
              <p className="text-sm text-[#737373]">Loading...</p>
            </div>
          ) : items.length > 0 ? (
            <div className="bg-transparent rounded-lg p-2">
              <ul className="space-y-3">
                {items.map((item, idx) => (
                  <li key={item.clarificationId}>
                    <button
                      type="button"
                      onClick={() => handleClickRow(item)}
                      className="w-full flex justify-between items-center  px-4 hover:bg-gray-50/50 rounded transition-colors text-left cursor-pointer"
                    >
                      <span className="text-sm text-[#262626] truncate flex-1 min-w-0 pr-3">{item.issueSummary}</span>
                      <span
                        className="flex items-center gap-1.5 shrink-0 px-2 h-6 rounded-full"
                        style={{ backgroundColor: 'rgba(229, 229, 229, 0.5)' }}
                      >
                        <span className="inline-block w-1.5 h-1.5 rounded-full bg-[#737373]" aria-hidden="true" />
                        <span className="text-xs text-[#737373] whitespace-nowrap">
                          {item.supplierCount} supplier{item.supplierCount !== 1 ? 's' : ''}
                        </span>
                      </span>
                    </button>
                    {idx < items.length - 1 && <div className="border-b border-[#E5E5E5] mt-3" />}
                  </li>
                ))}
              </ul>
            </div>
          ) : (
            /* 空状态：Figma 9942-136299 */
            <div className="flex items-center justify-center h-full min-h-[80px] gap-1">
              <CircleAlert size={18} className="text-[#737373]" />
              <p className="text-xs text-[#737373] tracking-wide">No manual intervention required</p>
            </div>
          )}
        </div>
      </section>

      {/* Clarification 回复弹窗 */}
      <ClarificationReplyDialog
        open={dialogOpen}
        items={items}
        selectedId={selectedId}
        onClose={handleDialogClose}
        onItemReplied={handleItemReplied}
        onAllDone={handleAllDone}
      />
    </>
  );
};

export default ClarificationBoard;
